// Cocoon site footer rendered in-tree by PageSkeleton (#33285 P3/P5).
// The page-level fine_print island is omitted (UtilityPage has no fine_print).
import { Immutable } from "immer";
import React from "react";
import { signupForEmailOffers } from "tsi-common-react/src/api/user";
import { config } from "tsi-common-react/src/config";
import { CaptchaLibrary } from "tsi-common-react/src/forms/Captcha/index";
import { jt, t } from "ttag";

import { Context } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { newTabAttrs } from "../NavLinkAttrs";

import styles from "./index.module.scss";

type ISocial = Immutable<{
    facebook: string;
    twitter: string;
    instagram: string;
    youtube: string;
}>;

type IFooterNav = NonNullable<
    React.ContextType<typeof Context>
>["chrome"]["footer_nav"];

type IFooterNavCategory = IFooterNav["columns"][number]["categories"][number];

type IProps = Immutable<{
    social: ISocial;
    copyrightYear: string;
    staticBase: string;
    footerNav: IFooterNav;
}>;

// Mobile accordion block: each category collapses/expands independently. State
// starts closed so SSR and first client render agree. The active class drives
// the SCSS open state (previously toggled imperatively by utility.ts).
const MobileFooterBlock = (
    props: Immutable<{ category: IFooterNavCategory }>,
): React.JSX.Element => {
    const { category } = props;
    const [open, setOpen] = React.useState(false);
    const toggle = () => {
        setOpen((prev) => !prev);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
        }
    };
    return (
        <dl className={open ? styles.mobileBlockActive : undefined}>
            <dt
                className={styles.mobileHeader}
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={toggle}
                onKeyDown={onKeyDown}
            >
                {category.title}
            </dt>
            <div className={styles.mobileDropdown}>
                {category.links.map((link, linkIdx) => (
                    <dd key={linkIdx}>
                        <a href={link.url} {...newTabAttrs(link)}>
                            {link.title}
                        </a>
                    </dd>
                ))}
            </div>
        </dl>
    );
};

export const SiteFooter = (props: IProps): React.JSX.Element => {
    const { social, copyrightYear, staticBase, footerNav } = props;
    const [email, setEmail] = React.useState("");
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitted, setSubmitted] = React.useState(false);
    const onEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            await signupForEmailOffers(email);
            setIsSubmitting(false);
            setSubmitted(true);
        } catch {
            setIsSubmitting(false);
        }
    };
    // Evaluate `t` per-render (not at module scope): a module-level `t` freezes
    // to the import-time locale and would serve English alt text on fr_CA pages
    // under SSR, where the Node process reuses module state across requests.
    const a360Alt = t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`;
    // Preserve the cosmetic styling <span> as a `jt` placeholder so word order
    // stays translatable without dropping the element.
    const bedroomOffers = <span key="offers">{t`bedroom offers`}</span>;
    const privacyTerms = (
        <a key="terms" href="https://policies.google.com/terms">
            {t`Privacy & Terms`}
        </a>
    );
    return (
        <footer className={styles.siteFooter}>
            {config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA") && <CaptchaLibrary />}
            <div className={styles.emailContainer}>
                <h3>{jt`Get exclusive ${bedroomOffers}`}</h3>
                {submitted ? (
                    <span className={styles.emailAlert} role="alert">
                        {t`Thank you!`}
                    </span>
                ) : (
                    /* Submitted client-side via the signupForEmailOffers API,
                       so this form never natively POSTs to Django -- no
                       action/method (and thus no CSRF token) is needed. */
                    <form id="footer-email-form" onSubmit={onEmailSubmit}>
                        <label
                            className="ada-screenreader-only"
                            htmlFor="footer-email-input"
                        >
                            {t`Email Address`}
                        </label>
                        <input
                            type="email"
                            id="footer-email-input"
                            name="footer-email"
                            placeholder={t`Email Address`}
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(event) => {
                                setEmail(event.currentTarget.value);
                            }}
                            disabled={isSubmitting}
                        />
                        <input
                            className="btn"
                            type="submit"
                            value={t`Submit`}
                            disabled={isSubmitting}
                        />
                    </form>
                )}
            </div>

            <div className={styles.linkContainer}>
                <nav>
                    {/* Desktop footer navigation */}
                    <div className={styles.navDesktop}>
                        {footerNav.columns.map((col, colIdx) => (
                            <div key={colIdx}>
                                {col.categories.map((cat, catIdx) => (
                                    <React.Fragment key={catIdx}>
                                        <h5>{cat.title}</h5>
                                        <ul>
                                            {cat.links.map((link, linkIdx) => (
                                                <li key={linkIdx}>
                                                    <a
                                                        href={link.url}
                                                        {...newTabAttrs(link)}
                                                    >
                                                        {link.title}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </React.Fragment>
                                ))}
                                {colIdx === footerNav.columns.length - 1 && (
                                    <button
                                        type="button"
                                        className={concatClassNames([
                                            styles.cookieSettings,
                                            "ot-sdk-show-settings",
                                        ])}
                                    >
                                        {t`Cookie Settings`}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {/* Mobile footer navigation */}
                    <div
                        className={styles.navMobile}
                        data-site={footerNav.site_hostname}
                        data-menu-name="footer"
                        data-menu-calling-page=""
                        data-menu-len={footerNav.menu_len}
                    >
                        {footerNav.columns.map((col, colIdx) => (
                            <React.Fragment key={colIdx}>
                                {col.categories.map((cat, catIdx) => (
                                    <MobileFooterBlock
                                        category={cat}
                                        key={catIdx}
                                    />
                                ))}
                                {colIdx === footerNav.columns.length - 1 && (
                                    <button
                                        type="button"
                                        className={concatClassNames([
                                            styles.cookieSettings,
                                            "ot-sdk-show-settings",
                                        ])}
                                    >
                                        {t`Cookie Settings`}
                                    </button>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <a
                        className={styles.badgeA360}
                        href="https://allyant.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            className="responsive-img"
                            src={`${staticBase}img/icons/badge-A360.png`}
                            alt={a360Alt}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </nav>
                <p className={styles.copyright}>
                    &copy; {copyrightYear} Sealy Technology LLC
                </p>
                <div className={styles.social}>
                    <ul>
                        <li>
                            <a
                                href={social.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <span className="ada-screenreader-only">
                                    {t`Cocoon by Sealy on Facebook`}
                                </span>
                                <div aria-hidden="true" />
                            </a>
                        </li>
                        <li>
                            <a
                                href={social.twitter}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t`Cocoon by Sealy Twitter`}
                            >
                                <span className="ada-screenreader-only">
                                    {t`Cocoon by Sealy on Twitter`}
                                </span>
                                <div
                                    className={styles.socialTwitter}
                                    aria-hidden="true"
                                />
                            </a>
                        </li>
                        <li>
                            <a
                                href={social.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t`Cocoon by Sealy Instagram`}
                            >
                                <span className="ada-screenreader-only">
                                    {t`Cocoon by Sealy on Instagram`}
                                </span>
                                <div
                                    className={styles.socialInstagram}
                                    aria-hidden="true"
                                />
                            </a>
                        </li>
                        <li>
                            <a
                                href={social.youtube}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t`Cocoon by Sealy YouTube`}
                            >
                                <span className="ada-screenreader-only">
                                    {t`Cocoon by Sealy on YouTube`}
                                </span>
                                <div
                                    className={styles.socialYoutube}
                                    aria-hidden="true"
                                />
                            </a>
                        </li>
                    </ul>
                </div>
                <div className={styles.notices}>
                    <p>
                        <a
                            href="/offer-details/"
                            className={styles.offerDetailsLink}
                        >
                            <strong>ˇ{t`See offer details.`}&nbsp;</strong>
                            {t`Restrictions and terms apply.`}
                        </a>
                    </p>
                    <p>
                        {t`*Based on OpenBrand survey for 12 months ending 12/31/2024.`}
                    </p>
                    <p>
                        <small>
                            {jt`This site is protected by reCAPTCHA (${privacyTerms}).`}
                        </small>
                    </p>
                </div>
            </div>
        </footer>
    );
};
